import { observer } from 'mobx-react'
import * as React from 'react'
import { Icon, Button } from '../../shared'
import { IReorderItem } from '../../../interfaces'
import { cartStore } from './../../../components/cart/cart.store'
import classNames from 'classnames'
import './reorder-button.scss'
import { modalStore } from '../../shared'
import { collectionsStore } from '../../collections/collections.store'
import { AlertIcon } from '../../collections/reorder-collection/alert-icon.component'
import { shopifyCartService } from '../../../services'

interface IProps {
  items: IReorderItem[]
  style?: 'link'
}

interface IState {
  loading: boolean
}

@observer
export class ReorderButton extends React.Component<IProps, IState> {
  state: IState = {
    loading: false
  }

  get itemURLParams(): string {
    return this.props.items.map((item): string => `${item.product.variantId}:${item.quantity}`).join(',')
  }

  getModalHeading(
    reOrderableOrderItems: IReorderItem[],
    unavailableOrderItems: IReorderItem[],
    outOfStockOrderItems: IReorderItem[]
  ): JSX.Element {
    if (reOrderableOrderItems.length !== this.props.items.length) {
      if (unavailableOrderItems.length === this.props.items.length) {
        // all the products are unavailable
        return (
          <>
            All products are
            <br />
            no longer on the menu
          </>
        )
      } else if (outOfStockOrderItems.length === this.props.items.length) {
        // all the products are out of stock
        return (
          <>
            All products are
            <br />
            out of stock
          </>
        )
      } else if (unavailableOrderItems.length + outOfStockOrderItems.length === this.props.items.length) {
        // all the products either out of stock or unavailable
        return (
          <>
            All products are
            <br />
            not available
          </>
        )
      } else if (
        unavailableOrderItems.length > 0 &&
        outOfStockOrderItems.length > 0 &&
        unavailableOrderItems.length + outOfStockOrderItems.length < this.props.items.length
      ) {
        // some products are out of stock; and some are unavailable
        return (
          <>
            Some products are
            <br />
            not available
          </>
        )
      } else if (unavailableOrderItems.length > 0) {
        // some products are unavailable
        return (
          <>
            Some products are
            <br />
            no longer on the menu
          </>
        )
      } else {
        // some products are out of stock
        return (
          <>
            Some products are
            <br />
            out of stock
          </>
        )
      }
    } else {
      return null
    }
  }

  getModalButton(
    reOrderableOrderItems: IReorderItem[],
    unavailableOrderItems: IReorderItem[],
    outOfStockOrderItems: IReorderItem[]
  ): JSX.Element {
    if (reOrderableOrderItems.length !== this.props.items.length) {
      if (
        unavailableOrderItems.length === this.props.items.length ||
        outOfStockOrderItems.length === this.props.items.length ||
        unavailableOrderItems.length + outOfStockOrderItems.length === this.props.items.length
      ) {
        return <>OK</>
      } else {
        return <>Continue</>
      }
    } else {
      return null
    }
  }

  openModal = (event: React.MouseEvent): void => {
    event.preventDefault()
    event.stopPropagation()

    const reOrderableOrderItems = collectionsStore.getReOrderableOrderItems(this.props.items)
    const unavailableOrderItems = collectionsStore.getUnavailableOrderItems(this.props.items)
    const outOfStockOrderItems = collectionsStore.getOutOfStockOrderItems(this.props.items)

    // Warning if there is any out of stock items
    if (reOrderableOrderItems.length !== this.props.items.length) {
      const outOfStockModal: JSX.Element = (
        <div
          style={{
            textAlign: 'center',
            padding: '30px',
            backgroundColor: `${
              outOfStockOrderItems.length > 0 && unavailableOrderItems.length === 0 ? 'rgba(255, 219, 167, 0.5)' : 'rgba(203, 5, 0, 0.1)'
            }`
          }}
        >
          <AlertIcon />
          <h3 style={{ fontSize: '24px', lineHeight: '38px', textTransform: 'uppercase' }}>
            {this.getModalHeading(reOrderableOrderItems, unavailableOrderItems, outOfStockOrderItems)}
          </h3>
          <p style={{ fontSize: '18px', lineHeight: '24px' }}>
            But, don't worry!
            <br />
            We have other products that you'll love.
          </p>
          <Button
            onClick={() => {
              if (reOrderableOrderItems.length !== 0) {
                this.addItemsToCart()
              }
              modalStore.toggle()
            }}
          >
            {this.getModalButton(reOrderableOrderItems, unavailableOrderItems, outOfStockOrderItems)}
          </Button>
        </div>
      )

      modalStore.set(outOfStockModal)
      modalStore.toggle()
    } else {
      this.addItemsToCart()
    }
  }

  async addItemsToCart() {
    if (this.state.loading) {
      return
    }

    this.setState({
      loading: true
    })

    cartStore.clearCart()

    collectionsStore.getReOrderableOrderItems(this.props.items).forEach((item: IReorderItem) => {
      cartStore.itemQuantityInputs.set(item.product.id, item.quantity.toString())
      // don't send anything to Shopify
      cartStore.updateOrAddItemToCart(item.product, item.quantity, false)
    })

    if (!cartStore.deliveryDate) {
      const deliveryDateModal: JSX.Element = (
        <div
          style={{
            textAlign: 'center',
            padding: '30px'
          }}
        >
          <h3 style={{ fontSize: '24px', lineHeight: '38px' }}>Delivery Date</h3>
          <p style={{ fontSize: '18px', lineHeight: '24px' }}>
            We just need you to select a delivery date for this order.
            <br />
            <br />
            We're now going to redirect you to the menu page so you can select a delivery date and continue to checkout.
          </p>
          <Button
            onClick={() => {
              window.location.href = '/collections/all'
            }}
          >
            Let's Go
          </Button>
        </div>
      )

      this.setState({
        loading: false
      })

      modalStore.set(deliveryDateModal)
      modalStore.toggle()
    } else {
      const response = await shopifyCartService.createCart(cartStore.formatCartInput)

      if (response) {
        location.href = `//${location.hostname}/checkout`
      } else {
        // @TODO This "shouldn't" ever happen, so log to Sentry
        this.setState({
          loading: false
        })
      }
    }
  }

  render(): JSX.Element {
    const buttonClasses: string = classNames('reorder-button btn btn--small', { 'btn--clear': this.props.style === 'link' })

    return (
      <a data-gtm-id={'order-reorder'} onClick={this.openModal} href={this.itemURLParams} className={buttonClasses}>
        <div className={this.state.loading ? 'hidden' : undefined}>
          Reorder <Icon icon='rollover' className='reorder-button__icon' />
        </div>

        {this.state.loading && (
          <div className='reorder-button__loading-wrapper'>
            <div className='reorder-button__loading-wrapper__spinner' />
          </div>
        )}
      </a>
    )
  }
}
